// World texture loading (v1 js/world-manager.js loadTextures used ~20 tiles
// from assets/textures/; v2 loads the load-bearing subset). Textures are
// loaded before the Game constructs the world so toon conversion sees the
// maps; every failure degrades to flat color.

import * as THREE from 'three';

export interface WorldTextures {
  asphalt: THREE.Texture | null;
  pavement: THREE.Texture | null;
  grass: THREE.Texture | null;
  brickRed: THREE.Texture | null;
  brickDark: THREE.Texture | null;
  wallBright: THREE.Texture | null;
}

function load(loader: THREE.TextureLoader, path: string): Promise<THREE.Texture | null> {
  return new Promise((resolve) => {
    loader.load(
      path,
      (texture) => {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.colorSpace = THREE.SRGBColorSpace;
        resolve(texture);
      },
      undefined,
      () => resolve(null),
    );
  });
}

export async function loadWorldTextures(): Promise<WorldTextures> {
  const loader = new THREE.TextureLoader();
  const [asphalt, pavement, grass, brickRed, brickDark, wallBright] = await Promise.all([
    load(loader, 'assets/textures/asphalt.jpg'),
    load(loader, 'assets/textures/pavement.jpg'),
    load(loader, 'assets/textures/grass.jpg'),
    load(loader, 'assets/textures/brickwall-red.jpg'),
    load(loader, 'assets/textures/brickwall-dark.jpg'),
    load(loader, 'assets/textures/wall-bright.jpg'),
  ]);
  return { asphalt, pavement, grass, brickRed, brickDark, wallBright };
}

/** Clone a texture with its own repeat. Clones are cached per (texture,
 *  repeat) — each clone is a separate GL texture, and world rebuilds call
 *  this again on every district switch. */
const repeatCache = new WeakMap<THREE.Texture, Map<string, THREE.Texture>>();

export function repeated(
  texture: THREE.Texture | null,
  x: number,
  y: number,
): THREE.Texture | null {
  if (!texture) return null;
  let perTexture = repeatCache.get(texture);
  if (!perTexture) {
    perTexture = new Map();
    repeatCache.set(texture, perTexture);
  }
  const key = `${x}:${y}`;
  const cached = perTexture.get(key);
  if (cached) return cached;
  const clone = texture.clone();
  clone.repeat.set(x, y);
  clone.needsUpdate = true;
  perTexture.set(key, clone);
  return clone;
}

/** Lambert material with optional texture; color tints the map (v1
 *  createTexturedMaterial behavior). */
export function texturedLambert(
  color: number,
  map: THREE.Texture | null,
): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color, ...(map ? { map } : {}) });
}
